import React, { useState, useEffect } from 'react';
import { func } from 'prop-types';
import { colors } from '@styleseat/brand';
import analytics from '../../../../modules/analytics';
import {
  Modal,
  Stack,
  Separator,
  HeaderText,
} from '../../../ui';
import { ProviderProductPropType } from '../ProfilePropTypes';
import FormButtonRow from '../ProviderProductUI/FormButtonRow';
import FormButtonCancel from '../ProviderProductUI/FormButtonCancel';
import FormButtonSubmit from '../ProviderProductUI/FormButtonSubmit';
import isErrorLink from './isErrorLink';
import TextFieldRow from '../ProviderProductUI/TextFieldRow';
import TextAreaRow from '../ProviderProductUI/TextAreaRow';

const URL_ERROR_MESSAGE = 'Invalid link. Your URL should start with http:// or https://';

const verifyUsableLink = link => link === ''
  || link.match(/http:\/\/.*\..*/)
  || link.match(/https:\/\/.*\..*/);

const useLinkErrorState = initialLink => {
  const [link, setLink] = useState(initialLink);
  const [isUsable, setIsUsable] = useState(false);
  const [errorText, setErrorText] = useState(null);

  useEffect(() => {
    if (!isErrorLink(link)) {
      setErrorText(null);
    } else {
      setErrorText(URL_ERROR_MESSAGE);
    }

    if (verifyUsableLink(link)) {
      setIsUsable(true);
    } else {
      setIsUsable(false);
    }
  }, [link]);

  return [
    link,
    setLink,
    isUsable,
    errorText,
  ];
};

const useCanSubmitForm = (title, linkIsUsable) => {
  const [canSubmit, setCanSubmit] = useState(false);

  useEffect(() => {
    if (title !== '' && linkIsUsable) {
      setCanSubmit(true);
    } else {
      setCanSubmit(false);
    }
  }, [title, linkIsUsable]);

  return [canSubmit];
};

export const ProviderProductForm = ({
  product,
  onSaveProduct,
  onClose,
  ...restProps
}) => {
  const [title, setTitle] = useState(product?.title || '');
  const [description, setDescription] = useState(product?.description || '');
  const [link, setLink, isUsableLink, linkErrorText] = useLinkErrorState(product?.link || '');
  const [canSubmit] = useCanSubmitForm(title, isUsableLink);

  const handleSave = () => {
    onSaveProduct({
      id: product?.id,
      title,
      description,
      link,
    });
    onClose();
    analytics.track('pro_saved_product', {
      product_name: title,
      description,
      link,
    });
  };

  return (
    <Modal
      onCloseRequest={onClose}
      renderTitle={() => (
        <HeaderText
          level="3"
          style={{
            flex: 1,
            paddingLeft: 36,
            textAlign: 'center',
          }}
        >
          Add Product
        </HeaderText>
      )}
      padding={0}
      {...restProps}
    >
      <Stack>
        <TextFieldRow
          value={title}
          name="title*"
          placeholder="Product Name"
          onChangeText={setTitle}
        />
        <Separator appearance="extrathin" color={colors.lightGrey} />
        <TextAreaRow
          name="description (optional)"
          placeholder="Product uses, attributes &amp; price"
          value={description}
          onChangeText={setDescription}
        />
        <Separator appearance="extrathin" color={colors.lightGrey} />
        <TextFieldRow
          value={link}
          error={linkErrorText}
          onChangeText={setLink}
          name="link (optional)"
          placeholder="http://example.com, https://example.com"
        />
        <Separator appearance="extrathin" color={colors.lightGrey} />
        <FormButtonRow>
          <FormButtonCancel
            onPress={onClose}
          >
            Cancel
          </FormButtonCancel>
          <FormButtonSubmit
            onPress={handleSave}
            disabled={!canSubmit}
          >
            Save
          </FormButtonSubmit>
        </FormButtonRow>
      </Stack>
    </Modal>
  );
};

ProviderProductForm.propTypes = {
  /** Called when the modal is closed */
  onClose: func.isRequired,
  /** Called when the user requests to save the product information */
  onSaveProduct: func.isRequired,
  product: ProviderProductPropType,
};

ProviderProductForm.defaultProps = {
  product: null,
};

export default ProviderProductForm;
